import { Button, Grid, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import { CurrencyCode } from 'further-types/lp/vehicle';
import GridContainer from 'components/GridContainer';
import ButtonGroup from 'components/ButtonGroup';
import EqualisationsTableHeader from 'components/EqualisationElements/EqualisationsTableHeader';
import Spacing from 'components/Spacing';
import ActionCard from './ActionCard';
import useLoadPartnerTransferActions from '../../hooks/useLoadPartnerTransferActions';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import api from 'lib/trpcClient';

type TransferActionsProps = {
  partnerTransferSection: PartnerTransferSection;
  partnerTransferId: string;
  vehicleId: string;
  baseCurrency: CurrencyCode;
  onPrevious: () => void;
  onNext: () => void;
};

const TransferActions: React.FC<TransferActionsProps> = ({
  partnerTransferSection,
  partnerTransferId,
  vehicleId,
  baseCurrency,
  onPrevious,
  onNext,
}) => {
  const { getValues, handleSubmit } =
    useFormContext<PartnerTransfersFormValues>();
  const transferDate = getValues('transferDate');

  const updateTransferMutation = api.partnerTransfers.update.useMutation();

  const handleNextClick = handleSubmit(
    async (formValues: PartnerTransfersFormValues) => {
      const input = {
        partnerTransferId,
        vehicleId,
        transferorCommitmentId: formValues.trasferrorCommitmentId,
        transferDate: formValues.transferDate,
        currentSection: partnerTransferSection,
        actions: formValues.actions ?? [],
        transferees: formValues.transferees.map((transferee) => ({
          investorId: transferee.investorId,
          amountLcy: transferee.amountLcy,
          percentage: transferee.percentage,
        })),
      };

      await updateTransferMutation.mutateAsync(input);

      onNext();
    },
  );

  const { actions } = useLoadPartnerTransferActions({
    partnerTransferId,
    vehicleId,
    partnerTransferSection,
  });

  const sectionLabels: Record<PartnerTransferSection, string> = {
    [PartnerTransferSection.PartnerTransfer]: 'partner transfer',
    [PartnerTransferSection.Investments]: 'investments',
    [PartnerTransferSection.Income]: 'income',
    [PartnerTransferSection.Expenses]: 'expenses',
    [PartnerTransferSection.LinkedCapitalActions]: 'linked capital actions',
    [PartnerTransferSection.GeneralCapitalActions]: 'general capital actions',
    [PartnerTransferSection.Fees]: 'fees',
    [PartnerTransferSection.Summary]: 'summary',
  };

  const hasActions = actions && actions.length > 0;

  return (
    <GridContainer>
      <Grid size={{ xs: 12, lg: 12 }}>
        {!hasActions ? (
          <Spacing size="lg">
            <Typography variant="body1">
              No {sectionLabels[partnerTransferSection]} found for this partner
              transfer.
            </Typography>
          </Spacing>
        ) : (
          <>
            <EqualisationsTableHeader
              headers={['Counterparty', 'Type', 'Date', 'Amount']}
              gridTemplateColumns="40% 20% 19% 19%"
            />

            {actions.map((action, index) => (
              <ActionCard
                key={index}
                actionIndex={index}
                isLastElement={index === actions.length - 1}
                action={action}
                baseCurrency={baseCurrency}
                partnerTransferSection={partnerTransferSection}
                transferDate={transferDate}
              />
            ))}
          </>
        )}
      </Grid>
      <Grid size={{ xs: 12, lg: 12 }}>
        <ButtonGroup>
          <Button variant="outlined" onClick={onPrevious}>
            Previous step
          </Button>

          <Button variant="contained" onClick={handleNextClick}>
            Save and continue
          </Button>
        </ButtonGroup>
      </Grid>
    </GridContainer>
  );
};

export default TransferActions;
